import React, { useEffect, useState } from 'react';
import { IconAlertCircle, IconShoppingCart } from './SVGIcons';
import CreditsAPI from '../../api/CreditsAPI';
import { Link } from 'react-router-dom';

interface InsufficientCreditsProps {
  onGoToPricing?: () => void;
  teamId: string;
  isOpen?: boolean;
  onClose?: () => void;
  type: 'analysis' | 'entity' | 'product';
  requiredCredits: number;
  usedCredits?: number;
  availableCredits?: number;
  remainingCredits?: number;
  isTrialing?: boolean;
  isAnalyzer?: boolean;
}

interface CreditsSummary {
  total_monthly_credits?: number;
  remaining_credits: number;
  db_size: number;
  product_db_size: number;
}

const InsufficientCredits: React.FC<InsufficientCreditsProps> = ({
  onGoToPricing,
  teamId,
  isOpen,
  onClose,
  type,
  requiredCredits,
  usedCredits,
  availableCredits,
  remainingCredits,
  isTrialing,
  isAnalyzer,
}) => {
  const [analysisCreditsAvailable, setAnalysisCreditsAvailable] = useState(0);
  const [databaseCreditsAvailable, setDatabaseCreditsAvailable] = useState(0);
  const [productCreditsAvailable, setProductCreditsAvailable] = useState(0);

  useEffect(() => {
    let active = true;

    const loadCreditsSummary = async () => {
      if (!teamId || !isOpen) {
        return;
      }

      const response = isTrialing
        ? await CreditsAPI.getTrialCreditsSummary(teamId)
        : await CreditsAPI.getCreditsSummary(teamId);
      if (!active || !response?.success) {
        return;
      }

      const creditsSummary = isTrialing
        ? (() => {
            const trialSummary = Array.isArray(response.result)
              ? response.result[0]
              : undefined;
            return {
              total_monthly_credits: Number(trialSummary?.credits_total ?? 0),
              remaining_credits: Number(trialSummary?.remaining_credits ?? 0),
              db_size: Number(trialSummary?.db_size ?? 0),
            } as CreditsSummary;
          })()
        : (response.result as CreditsSummary);
      setAnalysisCreditsAvailable(
        Number(creditsSummary?.remaining_credits ?? 0),
      );
      setDatabaseCreditsAvailable(Number(creditsSummary?.db_size ?? 0));
      setProductCreditsAvailable(Number(creditsSummary?.product_db_size ?? 0));
    };

    loadCreditsSummary();

    return () => {
      active = false;
    };
  }, [isOpen, teamId, isTrialing]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    if (isOpen && onClose) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const isRowCap = type === 'entity' || type === 'product';
  const teamCreditsAvailable =
    type === 'entity'
      ? databaseCreditsAvailable
      : type === 'product'
      ? productCreditsAvailable
      : analysisCreditsAvailable;
  const normalizedUsedCredits = Math.max(Number(usedCredits ?? 0), 0);
  const normalizedAvailableCredits = Math.max(
    Number(availableCredits ?? teamCreditsAvailable),
    0,
  );
  const normalizedRemainingCredits = Math.max(
    Number(
      remainingCredits ?? normalizedAvailableCredits - normalizedUsedCredits,
    ),
    0,
  );
  const deficit = Math.max(requiredCredits - teamCreditsAvailable, 0);

  return (
    <div className='cr-root' onClick={onClose}>
      <div
        className='cr-warning-card'
        onClick={(event) => event.stopPropagation()}
        role='dialog'
        aria-modal='true'
      >
        <div className='cr-warning-icon-wrap'>
          <IconAlertCircle />
        </div>
        <div>
          <h3 className='cr-warning-title'>Insufficient Credits</h3>
          <p className='cr-warning-subtitle'>
            Your team does not have enough credits to perform this action.
            Purchase additional credits to continue or contact your team admin.
          </p>
        </div>

        <div className='cr-stats-row'>
          <div className='cr-stat-block'>
            <span className='cr-stat-value'>
              {isRowCap ? normalizedUsedCredits : teamCreditsAvailable}
            </span>
            <span className='cr-stat-label'>
              {isRowCap ? 'Used' : 'Available'}
            </span>
          </div>
          <div className='cr-stat-block'>
            <span className='cr-stat-value'>
              {isRowCap ? normalizedAvailableCredits : requiredCredits}
            </span>
            <span className='cr-stat-label'>
              {isRowCap ? 'Total' : 'Required'}
            </span>
          </div>
          <div className='cr-stat-block'>
            <span className='cr-stat-value cr-stat-value-deficit'>
              {isRowCap ? normalizedRemainingCredits : deficit}
            </span>
            <span className='cr-stat-label'>
              {isRowCap ? 'Remaining' : 'Still Needed'}
            </span>
          </div>
        </div>

        <div className='cr-divider' />

        <Link
          to={{
            pathname: '/profile',
            state: { linkTab: 'Subscriptions' },
          }}
        >
          <button className='cr-btn-primary' onClick={onGoToPricing}>
            <IconShoppingCart /> Buy More Credits
          </button>
        </Link>

        {isAnalyzer && (
          <p className='cr-tip'>
            <strong>Note:</strong> The required credits for analyzers are
            estimated based on the number of entries in your upload before they
            get processed. If you are confident that you have enough credits,
            please reach out to info@bitedata.io with details of your upload and
            we can help investigate.
          </p>
        )}
      </div>
    </div>
  );
};

export default InsufficientCredits;
